/**
 * QuickBasketDrawer — persistent right-rail companion for the Options Lab.
 *
 * Phase C deliverable: picks legs from a preset (Long Straddle, Long Strangle,
 * Bull Call Spread, Bear Put Spread, Short Iron Condor) using the current
 * option chain, shows estimated cost / margin (heuristic), max P/L, and
 * executes each leg as a manual trade via the existing `/trades/manual` API.
 *
 * The margin formula is a heuristic — not a true SPAN call. For Indian
 * brokers the rough sell-side margin is `max(strike × 15%, premium × 5×)` per
 * lot. Buy-side margin is the premium paid. True SPAN is queued for a later
 * phase (would need broker API integration).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Stack, Chip, Button, Table, TableBody,
  TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip,
  Alert, Skeleton, InputBase,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import toast from 'react-hot-toast';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useConfirm } from '../common/ConfirmDialog';
import { tradingService } from '../../services/trading.service';
import { priceCcy } from '../../utils/optionExpiry';

interface ChainRow {
  strike: number;
  ceLtp?: number;
  peLtp?: number;
  ceDelta?: number;
  peDelta?: number;
}

interface LegTemplate {
  action: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  /** offset in strike-steps from ATM (negative = below spot, positive = above) */
  strikeOffset: number;
  qty?: number; // multiplier on lot size; default 1
}

interface PresetDef {
  id: string;
  name: string;
  description: string;
  legs: LegTemplate[];
  marketBias: 'NEUTRAL' | 'BULLISH' | 'BEARISH' | 'VOLATILITY_LONG' | 'VOLATILITY_SHORT';
}

const PRESETS: PresetDef[] = [
  {
    id: 'long_straddle',
    name: 'Long Straddle',
    description: 'BUY ATM CE + BUY ATM PE. Profits on a large move in either direction; loses on time decay if the underlying doesn\'t move.',
    marketBias: 'VOLATILITY_LONG',
    legs: [
      { action: 'BUY', optionType: 'CE', strikeOffset: 0 },
      { action: 'BUY', optionType: 'PE', strikeOffset: 0 },
    ],
  },
  {
    id: 'long_strangle',
    name: 'Long Strangle',
    description: 'BUY 1-step OTM CE + 1-step OTM PE. Cheaper than a straddle; needs a bigger move to be profitable.',
    marketBias: 'VOLATILITY_LONG',
    legs: [
      { action: 'BUY', optionType: 'CE', strikeOffset: 1 },
      { action: 'BUY', optionType: 'PE', strikeOffset: -1 },
    ],
  },
  {
    id: 'bull_call_spread',
    name: 'Bull Call Spread',
    description: 'BUY ATM CE + SELL OTM CE (2 strikes up). Defined-risk bullish bet; capped upside.',
    marketBias: 'BULLISH',
    legs: [
      { action: 'BUY',  optionType: 'CE', strikeOffset: 0 },
      { action: 'SELL', optionType: 'CE', strikeOffset: 2 },
    ],
  },
  {
    id: 'bear_put_spread',
    name: 'Bear Put Spread',
    description: 'BUY ATM PE + SELL OTM PE (2 strikes down). Defined-risk bearish bet; capped downside.',
    marketBias: 'BEARISH',
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeOffset: 0 },
      { action: 'SELL', optionType: 'PE', strikeOffset: -2 },
    ],
  },
  {
    id: 'short_iron_condor',
    name: 'Short Iron Condor',
    description: 'SELL OTM CE & PE + BUY further-OTM CE & PE wings. Premium-collection trade; profits in range-bound markets.',
    marketBias: 'VOLATILITY_SHORT',
    legs: [
      { action: 'SELL', optionType: 'PE', strikeOffset: -2 },
      { action: 'BUY',  optionType: 'PE', strikeOffset: -4 },
      { action: 'SELL', optionType: 'CE', strikeOffset:  2 },
      { action: 'BUY',  optionType: 'CE', strikeOffset:  4 },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  symbol: string;
  spot: number;
  chain: ChainRow[];
  lotSize: number;
  expiryLabel?: string; // e.g. "30-MAY-2026" — informational only
  feedSource?: string;  // DELTA → leg prices are USD; else INR
}

/** Find the ATM strike index (closest to spot). */
const atmIndex = (chain: ChainRow[], spot: number): number => {
  if (chain.length === 0) return -1;
  let best = 0; let bestDist = Infinity;
  for (let i = 0; i < chain.length; i++) {
    const d = Math.abs(chain[i].strike - spot);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
};

/** Estimate margin for one leg using a simple heuristic. */
const legMargin = (leg: ResolvedLeg, lotSize: number): number => {
  const lots = leg.qty * lotSize;
  const ltp = leg.ltp || 0;
  if (leg.action === 'BUY') {
    return ltp * lots; // debit = premium paid
  }
  // SELL: rough Indian-broker estimate
  const strikeBased = leg.strike * 0.15 * lots;
  const premiumBased = ltp * 5 * lots;
  return Math.max(strikeBased, premiumBased);
};

interface ResolvedLeg {
  action: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  ltp: number;
  qty: number; // lots
  margin: number;
  cost: number; // signed: positive = debit, negative = credit
}

const QuickBasketDrawer: React.FC<Props> = ({ open, onClose, symbol, spot, chain, lotSize, expiryLabel, feedSource }) => {
  const ccy = priceCcy(feedSource);
  const { C } = useAppTheme();
  const confirm = useConfirm();
  const [presetId, setPresetId] = useState<string>(PRESETS[0].id);
  const [qtyMul, setQtyMul] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const preset = PRESETS.find(p => p.id === presetId) || PRESETS[0];

  const legs: ResolvedLeg[] = useMemo(() => {
    if (chain.length === 0 || !spot) return [];
    const ai = atmIndex(chain, spot);
    if (ai < 0) return [];
    return preset.legs.map(t => {
      const idx = Math.max(0, Math.min(chain.length - 1, ai + t.strikeOffset));
      const row = chain[idx];
      const ltp = (t.optionType === 'CE' ? row.ceLtp : row.peLtp) || 0;
      const qty = (t.qty || 1) * qtyMul;
      const r: ResolvedLeg = {
        action: t.action,
        optionType: t.optionType,
        strike: row.strike,
        ltp,
        qty,
        margin: 0,
        cost: 0,
      };
      r.margin = legMargin(r, lotSize);
      r.cost = (t.action === 'BUY' ? 1 : -1) * ltp * qty * lotSize;
      return r;
    });
  }, [preset, chain, spot, qtyMul, lotSize]);

  const totals = useMemo(() => {
    const debit = legs.filter(l => l.action === 'BUY').reduce((s, l) => s + l.cost, 0);
    const credit = legs.filter(l => l.action === 'SELL').reduce((s, l) => s + Math.abs(l.cost), 0);
    const netPremium = debit - credit; // positive = net debit; negative = net credit
    const margin = legs.reduce((s, l) => s + l.margin, 0);
    // Heuristic max P/L (vertical-spread approximation)
    // For each pair (BUY+SELL of same type) max width × lot − net premium gives a rough cap.
    // Otherwise default to: max-profit = unbounded(BUYs) or net credit, max-loss = net debit / width.
    let maxProfit: number;
    let maxLoss: number;
    if (preset.id === 'bull_call_spread' || preset.id === 'bear_put_spread') {
      const strikes = legs.map(l => l.strike);
      const width = Math.abs(strikes[1] - strikes[0]) * lotSize * qtyMul;
      maxProfit = width - netPremium;
      maxLoss = netPremium;
    } else if (preset.id === 'short_iron_condor') {
      // wings (2-4): width × lot × qty − net credit
      const wing = Math.abs(legs[0].strike - legs[1].strike) * lotSize * qtyMul;
      maxProfit = -netPremium; // credit
      maxLoss = wing - (-netPremium);
    } else if (preset.id === 'long_straddle' || preset.id === 'long_strangle') {
      maxProfit = Infinity;
      maxLoss = netPremium;
    } else {
      maxProfit = NaN; maxLoss = NaN;
    }
    return { debit, credit, netPremium, margin, maxProfit, maxLoss };
  }, [legs, preset, lotSize, qtyMul]);

  const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  useEffect(() => {
    if (open) {
      // reset qty when opening
      setQtyMul(1);
    }
  }, [open]);

  const handleExecute = async () => {
    if (legs.length === 0) return;
    const ok = await confirm({
      title: `${preset.name} on ${symbol}?`,
      body: `${legs.length} legs will be placed as manual trades via the standard order pipeline. ` +
            `Net premium ${formatINR(totals.netPremium)}. Estimated margin ${formatINR(totals.margin)}. ` +
            `This bypasses the SignalCard's safety-gate flow — review each leg first.`,
      confirmLabel: 'Place basket',
      destructive: false,
    });
    if (!ok) return;
    setSubmitting(true);
    let placedCount = 0;
    try {
      for (const leg of legs) {
        // Symbol naming: we don't have a real options symbol resolver here; use a
        // descriptive composite. Broker integration that maps to real OPT symbols is
        // separate work; this records the intent in the trades table.
        const legSymbol = `${symbol} ${leg.strike}${leg.optionType}`;
        await tradingService.createManualTrade({
          symbol: legSymbol,
          exchange: 'NFO',
          side: leg.action === 'BUY' ? 'BUY' : 'SHORT',
          quantity: leg.qty,
          entryPrice: leg.ltp,
          tradeType: 'MARKET',
          notes: `${preset.name} leg · ${expiryLabel || ''}`.trim(),
        });
        placedCount++;
      }
      toast.success(`${preset.name}: ${placedCount} leg${placedCount === 1 ? '' : 's'} placed`);
      onClose();
    } catch (err: any) {
      toast.error(`Basket failed at leg ${placedCount + 1}: ${err?.response?.data?.error?.message || err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 2 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FlashOnIcon sx={{ color: C.accent }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Quick Basket</Typography>
          <Chip size="small" label={symbol} sx={{ ml: 0.5, fontWeight: 700, bgcolor: C.neutralMuted, color: C.neutral }} />
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {chain.length === 0 ? (
        <Alert severity="info">No option chain data loaded yet. Wait for the next poll, or open the Option Chain tab.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {/* Preset picker */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Strategy preset
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {PRESETS.map(p => (
                <Chip
                  key={p.id}
                  label={p.name}
                  onClick={() => setPresetId(p.id)}
                  size="small"
                  sx={{
                    fontWeight: presetId === p.id ? 700 : 500,
                    bgcolor: presetId === p.id ? C.accent : 'action.hover',
                    color: presetId === p.id ? C.onSolid : 'text.primary',
                    '&:hover': { bgcolor: presetId === p.id ? C.accent : 'action.selected' },
                  }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {preset.description}
            </Typography>
          </Box>

          {/* Lot multiplier */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              Lots
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={[1, 2, 5, 10].includes(qtyMul) ? qtyMul : null}
              onChange={(_, v) => v && setQtyMul(v)}
            >
              {[1, 2, 5, 10].map(q => <ToggleButton key={q} value={q}>{q}×</ToggleButton>)}
            </ToggleButtonGroup>
            <InputBase
              value={qtyMul}
              onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setQtyMul(Number.isFinite(n) && n > 0 ? n : 1); }}
              onFocus={e => e.target.select()}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'lots', style: { textAlign: 'center', padding: '2px 0', fontSize: '0.8rem', fontWeight: 700 } }}
              sx={{ width: 50, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5 }}
            />
            <Chip size="small" label={`Lot size ${lotSize}`} sx={{ ml: 0.5, fontSize: '0.66rem' }} />
            {expiryLabel && <Chip size="small" label={expiryLabel} sx={{ fontSize: '0.66rem' }} />}
          </Box>

          {/* Leg table */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Action', 'Type', 'Strike', 'LTP', 'Qty', 'Cost'].map(h => (
                    <TableCell key={h} sx={{ fontSize: '0.66rem', fontWeight: 700, py: 0.5, textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {legs.map((leg, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip
                        size="small"
                        label={leg.action}
                        sx={{
                          height: 18, fontSize: '0.62rem', fontWeight: 700,
                          bgcolor: leg.action === 'BUY' ? C.bullMuted : C.bearMuted,
                          color: leg.action === 'BUY' ? C.bull : C.bear,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontWeight: 700 }}>{leg.optionType}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontVariantNumeric: 'tabular-nums' }}>{leg.strike}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                      {Number(leg.ltp) > 0 ? `${ccy}${Number(leg.ltp).toFixed(ccy === '$' ? 2 : 1)}` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{leg.qty}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      color: leg.action === 'BUY' ? C.bear : C.bull }}>
                      {leg.cost >= 0 ? '+' : '−'}{formatINR(Math.abs(leg.cost))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Summary */}
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <Summary label="Net premium" value={`${totals.netPremium >= 0 ? 'Debit ' : 'Credit '}${formatINR(Math.abs(totals.netPremium))}`} tone={totals.netPremium >= 0 ? C.bear : C.bull} />
            <Summary label="Est. margin" value={formatINR(totals.margin)} tooltip="Heuristic: BUY = premium paid; SELL = max(strike × 15%, premium × 5×) per lot. Not a true SPAN call." />
            <Summary label="Max profit" value={Number.isFinite(totals.maxProfit) ? formatINR(totals.maxProfit) : 'Unbounded'} tone={C.bull} />
            <Summary label="Max loss" value={Number.isFinite(totals.maxLoss) ? formatINR(totals.maxLoss) : '—'} tone={C.bear} />
          </Box>

          <Alert severity="warning" icon={false} sx={{ py: 0.5, fontSize: '0.78rem' }}>
            <strong>Heads up.</strong> Margin and max P/L are heuristics. Verify against broker before placing. SPAN-accurate margin is queued for a later phase.
          </Alert>

          <Button
            variant="contained"
            size="large"
            onClick={handleExecute}
            disabled={submitting || legs.length === 0 || legs.some(l => l.ltp === 0)}
            sx={{ bgcolor: C.accent, fontWeight: 700, '&:hover': { bgcolor: C.accent, filter: 'brightness(1.1)' } }}
          >
            {submitting ? 'Placing…' : `Place ${preset.name} (${legs.length} legs)`}
          </Button>
          {legs.some(l => l.ltp === 0) && (
            <Typography variant="caption" color="text.secondary">One or more legs have no LTP yet — wait for next poll.</Typography>
          )}
        </Stack>
      )}
    </Drawer>
  );
};

const Summary: React.FC<{ label: string; value: string; tone?: string; tooltip?: string }> = ({ label, value, tone, tooltip }) => (
  <Tooltip arrow title={tooltip || ''}>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '0.92rem', color: tone || 'text.primary' }}>{value}</Typography>
    </Box>
  </Tooltip>
);

export default QuickBasketDrawer;
